import React, { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import Hr from 'components/Common/Hr'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import TextLink from 'LuxKit/TextLink'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import Typography from 'LuxKit/Typography/Typography'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import useToggle from 'hooks/useToggle'
import useItemTypeLabels from 'hooks/useItemTypeLabels'
import useOrderItemName from 'luxPlus/hooks/useOrderItemName'
import { useAppSelector } from 'hooks/reduxHooks'
import { groupBy, sortBy, take } from 'lib/array/arrayUtils'
import { randomIntegerBetween } from 'lib/maths/mathUtils'
import getLuxPlusRedeemedBenefits from 'luxPlus/selectors/benefits/getLuxPlusRedeemedBenefits'
import getLuxPlusTotalValueSaved from 'luxPlus/selectors/benefits/getLuxPlusTotalValueSaved'
import { useHumanReadableBenefitTypeLabel } from '../LuxPlusManageAccount/LuxPlusViewActivityLine'
import { ActivityIcon, getItemTypeIcon } from 'luxPlus/lib/itemTypeIcons'

const INITIAL_VISIBLE_COUNT = 5

const Section = styled(Pane)`
  padding: ${rem(20)};
  margin-top: ${rem(40)};
  margin-bottom: ${rem(64)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.L};
  scroll-margin-top: ${rem(100)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
  }
`

const MobileRow = styled.div`
  display: grid;
  grid-template-columns: ${rem(32)} minmax(0, 1fr) auto;
  gap: ${rem(4)} ${rem(12)};
  padding: ${rem(8)} ${rem(12)};
  align-items: center;
`

const MobileRowName = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MobileRowMeta = styled.div`
  grid-column: 2 / -1;
`

const MobileYearHeader = styled(Pane)`
  padding: ${rem(6)} ${rem(12)};
`

const TabletRow = styled.div`
  display: grid;
  gap: ${rem(8)};
  grid-template-columns: ${rem(32)} minmax(0, 1fr) ${rem(120)} ${rem(140)} ${rem(100)};
  padding: ${rem(8)} ${rem(16)};
  align-items: center;
`

const TotalRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${rem(16)};
  padding: ${rem(16)} ${rem(20)};
  background: ${props => props.theme.palette.highlight.secondary.lightBackground};
`

interface ActivityRowProps {
  item: App.RedeemedMembershipBenefit
  isLast: boolean
}

function ActivityRow({ item, isLast }: ActivityRowProps) {
  const intl = useIntl()
  const humanReadableBenefitTypeLabel = useHumanReadableBenefitTypeLabel()
  const itemTypeLabels = useItemTypeLabels()
  const benefitType = humanReadableBenefitTypeLabel[item.subtype ?? ''] ?? humanReadableBenefitTypeLabel[item.type] ?? ''

  const fallbackLabel = intl.formatMessage({
    id: 'ODsT070Qa56A6Cp2fGZr',
    defaultMessage: 'Booking',
    description: 'Placeholder label for a redeemed benefit when the order name cannot be resolved',
  })

  const { name: orderName, fetching } = useOrderItemName(item.orderId, item.itemId)
  const itemName = orderName || item.description || itemTypeLabels[item.itemType] || fallbackLabel

  const Icon = getItemTypeIcon(item.itemType)

  const multiplierDisplay = <FormattedMessage
    id="jTpZY5EBs_Jg8rwZmWs1"
    defaultMessage="{value}x"
    description="Multiplier value display for unit-based membership benefits"
    values={{ value: <FormatDecimal value={item.adjustment} /> }}
  />

  const valueDisplay = item.unit === 'currency' && item.adjustment > 0
    ? <FormatCurrency format="rounded" value={item.adjustment} />
    : item.unit === 'units' && item.adjustment > 1
      ? multiplierDisplay
      : benefitType

  return <>
    <CSSBreakpoint max="mobile">
      <MobileRow>
        <ActivityIcon><Icon /></ActivityIcon>
        <MobileRowName>
          {fetching && <TextLoadingBox width={`${randomIntegerBetween(8, 14)}ch`} />}
          {!fetching && <BodyText variant="medium" weight="bold" wrap="truncate">{itemName}</BodyText>}
        </MobileRowName>
        <BodyText weight="bold" colour="lux-plus" variant="medium" wrap="no-wrap" align="end">
          {valueDisplay}
        </BodyText>
        <MobileRowMeta>
          <Caption colour="neutral-three" variant="large">
            <FormattedMessage
              id="r58QyhAXdIF_C3xjhQlq"
              defaultMessage="{date} · {benefitType}"
              description="Activity row metadata showing date and benefit type separated by a middle dot"
              values={{
                date: <FormatDateTime value={item.updatedAt} format="dmy-casual" />,
                benefitType,
              }}
            />
          </Caption>
        </MobileRowMeta>
      </MobileRow>
      {!isLast && <Hr size="xs" />}
    </CSSBreakpoint>
    <CSSBreakpoint min="tablet">
      <TabletRow>
        <ActivityIcon><Icon /></ActivityIcon>
        {fetching && <TextLoadingBox width={`${randomIntegerBetween(8, 14)}ch`} />}
        {!fetching && <BodyText variant="medium" weight="bold" wrap="truncate">{itemName}</BodyText>}
        <BodyText colour="neutral-two" wrap="no-wrap" variant="medium">
          <FormatDateTime value={item.updatedAt} format="dmy-casual" />
        </BodyText>
        <BodyText variant="medium" colour="neutral-two">{benefitType}</BodyText>
        <BodyText align="end" weight="bold" colour="highlight-secondary" variant="medium">
          {valueDisplay}
        </BodyText>
      </TabletRow>
      {!isLast && <Hr size="xs" />}
    </CSSBreakpoint>
  </>
}

function LuxPlusActivityTable() {
  const intl = useIntl()
  const confirmedItems = useAppSelector(getLuxPlusRedeemedBenefits)
  const totalSavings = useAppSelector(getLuxPlusTotalValueSaved)
  const [showMore, toggleShowMore] = useToggle(false)

  const sortedItems = useMemo(() =>
    sortBy([...confirmedItems], item => new Date(item.updatedAt), 'desc'),
  [confirmedItems])

  const activitiesByYear = useMemo(() => {
    let visibleCount = showMore ? sortedItems.length : INITIAL_VISIBLE_COUNT
    if (!showMore && sortedItems.length === INITIAL_VISIBLE_COUNT + 1) {
      visibleCount = INITIAL_VISIBLE_COUNT + 1
    }
    const groups = groupBy(take(sortedItems, visibleCount), item => new Date(item.updatedAt).getFullYear())
    return Array.from(groups.values()).map(group => sortBy(group, item => new Date(item.updatedAt), 'desc'))
  }, [sortedItems, showMore])

  if (!confirmedItems.length) return null

  const showMoreLabel = intl.formatMessage({
    id: 'GXecyvPsoHMdElf863WQ',
    defaultMessage: 'Show more',
    description: 'Button to show more activity items in benefits table',
  })

  const showLessLabel = intl.formatMessage({
    id: '-43ubtvia_v0bCQjLdeX',
    defaultMessage: 'Show less',
    description: 'Link text to collapse and show fewer loyalty activities',
  })

  const disclaimerText = intl.formatMessage({
    id: 'C55euLFotIgAhhlnNz2b',
    defaultMessage: 'Please note some transactions may take up to 24hrs to appear after your booking is completed.',
    description: 'Footnote explaining delay in activity table transactions',
  })

  const benefitColumnLabel = intl.formatMessage({
    id: '78VnVH6hkCEwWmhJcgjZ',
    defaultMessage: 'Benefit',
    description: 'Column header for benefit type in membership activity table',
  })

  const valueColumnLabel = intl.formatMessage({
    id: 'fxjXR1i38aMhIKamSmuf',
    defaultMessage: 'Savings',
    description: 'Column header for saved savings amount in membership activity table',
  })

  return <Section id="luxplus-your-activity">
    <VerticalSpacer gap={16}>
      <Heading variant="heading3">
        {intl.formatMessage({
          id: 'EKcw0jibbYq8uI_3zWTj',
          defaultMessage: 'Membership activity',
          description: 'Title for the membership activity modal showing redeemed benefits',
        })}
      </Heading>

      <VerticalSpacer gap={20}>
        {activitiesByYear.map(items => <VerticalSpacer gap={8} key={items[0].updatedAt}>
          <CSSBreakpoint max="mobile">
            <MobileYearHeader background="neutral-seven">
              <Caption variant="large" weight="bold" colour="neutral-three">
                <FormatDateTime value={items[0].updatedAt} year="numeric" />
              </Caption>
            </MobileYearHeader>
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet">
            <TabletRow as={Pane} background="neutral-seven">
              <Caption variant="large" weight="bold" colour="neutral-three" style={{ gridColumn: 'span 2' }}>
                <FormatDateTime value={items[0].updatedAt} year="numeric" />
              </Caption>
              <Caption variant="large" weight="bold" colour="neutral-three"></Caption>
              <Caption variant="large" weight="bold" colour="neutral-two">{benefitColumnLabel}</Caption>
              <Caption variant="large" weight="bold" colour="neutral-two" align="end">{valueColumnLabel}</Caption>
            </TabletRow>
          </CSSBreakpoint>
          <Group direction="vertical">
            {items.map((item, idx) => <ActivityRow
              key={item.id}
              item={item}
              isLast={idx === items.length - 1}
            />)}
          </Group>
        </VerticalSpacer>)}
      </VerticalSpacer>

      {sortedItems.length > INITIAL_VISIBLE_COUNT + 1 && <Typography align="center">
        <TextLink size="caption" onClick={toggleShowMore}>
          {showMore ? showLessLabel : showMoreLabel}
        </TextLink>
      </Typography>}

      {totalSavings > 0 && (
        <TotalRow>
          <BodyText variant="large" colour="highlight-secondary" weight="bold">
            {intl.formatMessage({
              id: 'bN8WXrAs6AHWeb7W04b8',
              defaultMessage: 'Total value saved',
              description: 'Label for the total savings amount in the membership activity modal',
            })}
          </BodyText>
          <BodyText variant="large" colour="highlight-secondary" weight="bold" align="end">
            <FormatCurrency format="roundedDollar" value={totalSavings} />
          </BodyText>
        </TotalRow>
      )}

      <BodyText variant="small" colour="neutral-two" align="center">
        {disclaimerText}
      </BodyText>
    </VerticalSpacer>
  </Section>
}

export default LuxPlusActivityTable
